/**
 * Native method-availability registry — the display layer's gate for features
 * that depend on a bridge method the current shell may or may not implement.
 *
 * The handshake response carries `supportedMethods` (the native BridgeRouter's
 * registered method list). workoutsStore captures it on boot; components read
 * `supportsMethod(name)` to decide whether a call site is live. This is how a
 * newer web bundle can ship a call to a method (e.g. `openRouteMap`) that an
 * OLDER native shell doesn't have yet WITHOUT dead-ending: the gate is false
 * until a native handshake actually lists the method.
 *
 * In a plain browser / screenshot harness with no native handshake, the set
 * stays empty, so any method-gated affordance is hidden — it can never appear
 * where tapping it would go nowhere.
 */
import { writable, derived, get } from "svelte/store";
import type { NativeWebBuild } from "./buildInfo";

export const supportedMethods = writable<string[]>([]);

/**
 * Which web build the SHELL says it painted (handshake `webBuildOrigin` /
 * `webBuildIsLatest`). Null in a browser, and on any shell older than 2026-07-13.
 *
 * The page cannot work this out for itself: a build downloaded over the air and a build
 * compiled into the app are both `file://` loads. Only the shell knows which one it opened,
 * and the Save-health line must not guess — see buildSourceLabel().
 */
export const nativeWebBuild = writable<NativeWebBuild | null>(null);

/** Record the handshake's answer. Absent fields → null (unknown), never a guess. */
export function setNativeWebBuild(hs: NativeWebBuild | null | undefined): void {
  if (!hs || (hs.origin == null && hs.isLatest == null)) {
    nativeWebBuild.set(null);
    return;
  }
  nativeWebBuild.set({ origin: hs.origin ?? null, isLatest: hs.isLatest ?? null });
}

/** Record the handshake's method list. Idempotent; last write wins. */
export function setSupportedMethods(methods: string[] | undefined | null): void {
  supportedMethods.set(Array.isArray(methods) ? methods : []);
}

/** Reactive: true once a native handshake has listed `method`. */
export function supportsMethodStore(method: string) {
  return derived(supportedMethods, ($m) => $m.includes(method));
}

/** Imperative one-shot check (non-reactive). */
export function supportsMethod(method: string): boolean {
  return get(supportedMethods).includes(method);
}

/** TEST-ONLY reset. */
export function _resetSupportedMethods(): void {
  supportedMethods.set([]);
  nativeWebBuild.set(null);
}
